/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable no-use-before-define */
import type { Vector2 } from '../vector/Vector2';
import { Vector3 } from '../vector/Vector3';
import type { Plane } from './Plane';

export class Triangle {
  public a: Vector3;
  public b: Vector3;
  public c: Vector3;

  constructor(a?: Vector3, b?: Vector3, c?: Vector3) {
    this.a = a !== undefined ? a : new Vector3();
    this.b = b !== undefined ? b : new Vector3();
    this.c = c !== undefined ? c : new Vector3();
  }

  public static getNormal(
    a: Vector3,
    b: Vector3,
    c: Vector3,
    out: Vector3,
  ): Vector3 {
    if (out === undefined) {
      console.warn('THREE.Triangle: .getNormal() target is now required');
      out = new Vector3();
    }

    out.subVectors(c, b);
    v0.subVectors(a, b);
    out.cross(v0);

    const targetLengthSq = out.lengthSq();

    if (targetLengthSq > 0) {
      return out.multiplyScalar(1 / Math.sqrt(targetLengthSq));
    }

    return out.set(0, 0, 0);
  }

  // static/instance method to calculate barycentric coordinates
  // based on: http://www.blackpawn.com/texts/pointinpoly/default.html
  public static getBarycoord(
    point: Vector3,
    a: Vector3,
    b: Vector3,
    c: Vector3,
    out: Vector3,
  ): Vector3 {
    v0.subVectors(c, a);
    v1.subVectors(b, a);
    v2.subVectors(point, a);

    const dot00 = v0.dot(v0);
    const dot01 = v0.dot(v1);
    const dot02 = v0.dot(v2);
    const dot11 = v1.dot(v1);
    const dot12 = v1.dot(v2);

    const denom = dot00 * dot11 - dot01 * dot01;

    // collinear or singular triangle
    if (denom === 0) {
      // arbitrary location outside of triangle?
      // not sure if this is the best idea, maybe should be returning undefined
      return out.set(-2, -1, -1);
    }

    const invDenom = 1 / denom;
    const u = (dot11 * dot02 - dot01 * dot12) * invDenom;
    const v = (dot00 * dot12 - dot01 * dot02) * invDenom;

    // barycentric coordinates must always sum to 1
    return out.set(1 - u - v, v, u);
  }

  public static containsPoint(
    point: Vector3,
    a: Vector3,
    b: Vector3,
    c: Vector3,
  ): boolean {
    Triangle.getBarycoord(point, a, b, c, v1);

    return v1.x >= 0 && v1.y >= 0 && v1.x + v1.y <= 1;
  }

  public static getUV(
    point: Vector3,
    p1: Vector3,
    p2: Vector3,
    p3: Vector3,
    uv1: Vector2,
    uv2: Vector2,
    uv3: Vector2,
    out: Vector2,
  ): Vector2 {
    const barycoord = v2;

    this.getBarycoord(point, p1, p2, p3, barycoord);

    out.set(0, 0);
    out.addScaledVector(uv1, barycoord.x);
    out.addScaledVector(uv2, barycoord.y);
    out.addScaledVector(uv3, barycoord.z);

    return out;
  }

  public set(a: Vector3, b: Vector3, c: Vector3): this {
    this.a.copy(a);
    this.b.copy(b);
    this.c.copy(c);

    return this;
  }

  public clone(): Triangle {
    return new Triangle().copy(this);
  }

  public copy(triangle: Triangle): this {
    this.a.copy(triangle.a);
    this.b.copy(triangle.b);
    this.c.copy(triangle.c);

    return this;
  }

  public getArea(): number {
    v0.subVectors(this.c, this.b);
    v1.subVectors(this.a, this.b);

    return v0.cross(v1).length() * 0.5;
  }

  public getMidpoint(out: Vector3): Vector3 {
    return out
      .addVectors(this.a, this.b)
      .add(this.c)
      .multiplyScalar(1 / 3);
  }

  public getNormal(out: Vector3): Vector3 {
    return Triangle.getNormal(this.a, this.b, this.c, out);
  }

  public getPlane(out: Plane): Plane {
    return out.setFromCoplanarPoints(this.a, this.b, this.c);
  }

  public getBarycoord(point: Vector3, out: Vector3): Vector3 {
    return Triangle.getBarycoord(point, this.a, this.b, this.c, out);
  }

  public containsPoint(point: Vector3): boolean {
    return Triangle.containsPoint(point, this.a, this.b, this.c);
  }

  public getUV(
    point: Vector3,
    uv1: Vector2,
    uv2: Vector2,
    uv3: Vector2,
    out: Vector2,
  ): Vector2 {
    return Triangle.getUV(point, this.a, this.b, this.c, uv1, uv2, uv3, out);
  }

  // TODO fix if required
  // intersectsBox(box: Box3)
  // {
  //     return box.intersectsTriangle(this);
  // }

  public closestPointToPoint(p: Vector3, out: Vector3): Vector3 {
    const a = this.a;
    const b = this.b;
    const c = this.c;
    let v;
    let w;

    // algorithm thanks to Real-Time Collision Detection by Christer Ericson,
    // published by Morgan Kaufmann Publishers, (c) 2005 Elsevier Inc.,
    // under the accompanying license; see chapter 5.1.5 for detailed explanation.
    // basically, we're distinguishing which of the voronoi regions of the triangle
    // the point lies in with the minimum amount of redundant computation.

    vab.subVectors(b, a);
    vac.subVectors(c, a);
    vap.subVectors(p, a);
    const d1 = vab.dot(vap);
    const d2 = vac.dot(vap);

    if (d1 <= 0 && d2 <= 0) {
      // vertex region of A; barycentric coords (1, 0, 0)
      return out.copy(a);
    }

    vbp.subVectors(p, b);
    const d3 = vab.dot(vbp);
    const d4 = vac.dot(vbp);

    if (d3 >= 0 && d4 <= d3) {
      // vertex region of B; barycentric coords (0, 1, 0)
      return out.copy(b);
    }

    const vc = d1 * d4 - d3 * d2;

    if (vc <= 0 && d1 >= 0 && d3 <= 0) {
      v = d1 / (d1 - d3);

      // edge region of AB; barycentric coords (1-v, v, 0)
      return out.copy(a).addScaledVector(vab, v);
    }

    vcp.subVectors(p, c);
    const d5 = vab.dot(vcp);
    const d6 = vac.dot(vcp);

    if (d6 >= 0 && d5 <= d6) {
      // vertex region of C; barycentric coords (0, 0, 1)
      return out.copy(c);
    }

    const vb = d5 * d2 - d1 * d6;

    if (vb <= 0 && d2 >= 0 && d6 <= 0) {
      w = d2 / (d2 - d6);

      // edge region of AC; barycentric coords (1-w, 0, w)
      return out.copy(a).addScaledVector(vac, w);
    }

    const va = d3 * d6 - d5 * d4;

    if (va <= 0 && d4 - d3 >= 0 && d5 - d6 >= 0) {
      vbc.subVectors(c, b);
      w = (d4 - d3) / (d4 - d3 + (d5 - d6));

      // edge region of BC; barycentric coords (0, 1-w, w)
      return out.copy(b).addScaledVector(vbc, w); // edge region of BC
    }

    // face region
    const denom = 1 / (va + vb + vc);
    // u = va * denom

    v = vb * denom;
    w = vc * denom;

    return out.copy(a).addScaledVector(vab, v).addScaledVector(vac, w);
  }

  public equals(triangle: Triangle): boolean {
    return (
      triangle.a.equals(this.a) &&
      triangle.b.equals(this.b) &&
      triangle.c.equals(this.c)
    );
  }
}

const v0 = new Vector3();
const v1 = new Vector3();

const v2 = new Vector3();

const vab = new Vector3();
const vac = new Vector3();
const vbc = new Vector3();
const vap = new Vector3();
const vbp = new Vector3();
const vcp = new Vector3();
